import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  TaxForecastProfileFamilySituation,
  TaxForecastProfileTaxCalculationMode,
} from '__generated__/GQL';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';

import { StandardTaxTypeSchema } from '../../taxType.schema';
import { locales } from './locales';

enum SingleParent {
  YES = 'YES',
  NO = 'NO',
}

const convertSingleParentValueToFormValue = (
  isSingleParent: StandardTaxTypeSchema['isSingleParent'],
): SingleParent | undefined => {
  if (isSingleParent === null) {
    return undefined;
  }

  return isSingleParent === true ? SingleParent.YES : SingleParent.NO;
};

interface StandardCardProps {
  isSelected: boolean;
}

const StandardCard: FC<StandardCardProps> = ({ isSelected }) => {
  const {
    control,
    formState: { errors },
    register,
    watch,
  } = useFormContext<StandardTaxTypeSchema>();

  const numberOfChildren = watch('numberOfChildren');
  const familySituation = watch('familySituation');

  const areSingleParentRadiosDisabled =
    !numberOfChildren ||
    numberOfChildren < 0 ||
    familySituation === TaxForecastProfileFamilySituation.Couple;

  return (
    <SunshineCard.Group isActive={isSelected}>
      <SunshineCard
        title={locales.title}
        value={TaxForecastProfileTaxCalculationMode.Standard}
        variant="radio"
      />

      {isSelected ? (
        <SunshineCard>
          <SunshineCard.Content alignItems="stretch" gap="space-16">
            <SunshineCard.Title>{locales.subtitle}</SunshineCard.Title>

            <Form.Field
              error={errors.numberOfChildren?.message}
              label={locales.numberOfChildren}
            >
              <Form.Input
                {...register('numberOfChildren', {
                  valueAsNumber: true,
                })}
                inputMode="numeric"
                onFocus={(e) => e.target.select()}
                placeholder="0"
                type="number"
              />
            </Form.Field>

            <Controller
              control={control}
              name="familySituation"
              render={({ field: { onChange, value } }) => (
                <Form.Field label={locales.familySituation}>
                  <SunshineCard.RadioGroup onChange={onChange} value={value}>
                    <SunshineCard.Group>
                      <SunshineCard
                        title={locales.single}
                        value={TaxForecastProfileFamilySituation.Single}
                        variant="radio"
                      />
                      <SunshineCard
                        title={locales.coupled}
                        value={TaxForecastProfileFamilySituation.Couple}
                        variant="radio"
                      />
                      <SunshineCard
                        title={locales.widowed}
                        value={TaxForecastProfileFamilySituation.Widow}
                        variant="radio"
                      />
                    </SunshineCard.Group>
                  </SunshineCard.RadioGroup>
                </Form.Field>
              )}
            />

            <Controller
              control={control}
              name="isSingleParent"
              render={({ field: { onChange, value } }) => (
                <Form.Field label={locales.singleParent}>
                  <Form.Radio.Group
                    direction="row"
                    onChange={(newValue: SingleParent) =>
                      onChange(newValue === SingleParent.YES ? true : false)
                    }
                    radioPosition="right"
                    value={convertSingleParentValueToFormValue(value)}
                  >
                    <Form.Radio
                      isDisabled={areSingleParentRadiosDisabled}
                      value={SingleParent.YES}
                      variant="outline"
                    >
                      <Form.Radio.Title>{locales.yes}</Form.Radio.Title>
                    </Form.Radio>

                    <Form.Radio
                      isDisabled={areSingleParentRadiosDisabled}
                      value={SingleParent.NO}
                      variant="outline"
                    >
                      <Form.Radio.Title>{locales.no}</Form.Radio.Title>
                    </Form.Radio>
                  </Form.Radio.Group>
                </Form.Field>
              )}
            />

            <Form.Field
              error={errors.additionalHouseholdRevenue?.message}
              label={locales.otherIncome}
            >
              <Form.Input
                inputMode="decimal"
                onFocus={(e) => e.target.select()}
                placeholder="0"
                rightIcon="euro"
                type="number"
                {...register('additionalHouseholdRevenue', {
                  valueAsNumber: true,
                })}
              />
            </Form.Field>
          </SunshineCard.Content>
        </SunshineCard>
      ) : null}
    </SunshineCard.Group>
  );
};

export default StandardCard;
